/** @ken/module-purchase-orders — frontend entry point.
 *
 *  0.2.0 — story-page adoption (ADR-0081). The in-list Sheet is RETIRED for
 *  purchase orders; `purchase-orders/$id` is a full-page story shell with
 *  routed tab children (overview / line-items / related / notes) and an index
 *  redirect to …/overview. Mirrors the shipped invoicing adoption.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { PurchaseOrderStorySearch } from "./routes/story/purchase-order-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadPurchaseOrderStoryTabs = () => import("./routes/story/purchase-order-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const PurchaseOrderCreatePage = lazyScreen(
  () => import("./routes/purchase-order-create"),
  "PurchaseOrderCreatePage",
);
const PurchaseOrderEditPage = lazyScreen(
  () => import("./routes/purchase-order-edit"),
  "PurchaseOrderEditPage",
);
const PurchaseOrderListPage = lazyScreen(
  () => import("./routes/purchase-order-list"),
  "PurchaseOrderListPage",
);
const PurchaseOrderStoryDetail = lazyScreen(
  () => import("./routes/story/purchase-order-story-detail"),
  "PurchaseOrderStoryDetail",
);
const PurchaseOrderActivityRoute = lazyScreen(
  loadPurchaseOrderStoryTabs,
  "PurchaseOrderActivityRoute",
);
const PurchaseOrderLineItemsRoute = lazyScreen(
  loadPurchaseOrderStoryTabs,
  "PurchaseOrderLineItemsRoute",
);
const PurchaseOrderNotesRoute = lazyScreen(
  loadPurchaseOrderStoryTabs,
  "PurchaseOrderNotesRoute",
);
const PurchaseOrderOverviewRoute = lazyScreen(
  loadPurchaseOrderStoryTabs,
  "PurchaseOrderOverviewRoute",
);
const PurchaseOrderRelatedRoute = lazyScreen(
  loadPurchaseOrderStoryTabs,
  "PurchaseOrderRelatedRoute",
);

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface PurchaseOrderListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "purchase-orders",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof; the manual writeQueryParams is retired.
    validateSearch: (
      raw: Record<string, unknown>,
    ): PurchaseOrderListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: PurchaseOrderListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "purchase-orders/new",
    component: PurchaseOrderCreatePage,
  });

  // Edit (backlog B2): a focused FULL-PAGE form, deep-linkable, NOT nested
  // under the story shell. Parented directly to the module root so it renders
  // standalone (no StoryPageDetail header/tabs around it). Registered BEFORE
  // the `purchase-orders/$id` parent so the `/edit` literal segment is matched.
  const editRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "purchase-orders/$id/edit",
    component: PurchaseOrderEditPage,
  });

  // ── Story-page detail (ADR-0081): parent purchase-orders/$id shell +
  //    routed tab children + an index redirect to …/overview. ──────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "purchase-orders/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (
      raw: Record<string, unknown>,
    ): PurchaseOrderStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: PurchaseOrderStoryDetail,
  });

  // Index redirect purchase-orders/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/purchase-orders/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: PurchaseOrderOverviewRoute,
  });
  const lineItemsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "line-items",
    component: PurchaseOrderLineItemsRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: PurchaseOrderRelatedRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: PurchaseOrderActivityRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: PurchaseOrderNotesRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      lineItemsRoute,
      relatedRoute,
      activityRoute,
      notesRoute,
    ],
  );

  return [listRoute, createRouteEntry, editRoute, detailRoute];
};
